// 어드민 API 공통 클라이언트. 인증 토큰 공급자는 #8에서 연결한다.

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  constructor(message: string, status: number, code: string | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export type TokenProvider = () => Promise<string | null>;

export interface ApiClientOptions {
  baseUrl?: string;
  getToken?: TokenProvider;
  fetchImpl?: typeof fetch;
}

export function createApiClient({ baseUrl = '', getToken = async () => null, fetchImpl }: ApiClientOptions = {}) {
  const root = baseUrl.replace(/\/+$/, '') || '/api';
  const doFetch = fetchImpl ?? ((...args: Parameters<typeof fetch>) => fetch(...args));

  async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = await getToken();
    const headers = new Headers(init.headers);
    headers.set('Accept', 'application/json');
    if (init.body !== undefined && !(init.body instanceof FormData)) headers.set('Content-Type', 'application/json');
    if (token) headers.set('Authorization', `Bearer ${token}`);

    let res: Response;
    try {
      res = await doFetch(`${root}${path.startsWith('/') ? path : `/${path}`}`, { ...init, headers });
    } catch {
      throw new ApiError('서버에 연결할 수 없습니다.', 0, 'network');
    }

    const text = await res.text();
    let body: unknown = null;
    if (text) {
      try {
        body = JSON.parse(text);
      } catch {
        body = null;
      }
    }
    if (!res.ok) {
      const err = (body as { error?: { message?: string; code?: string } } | null)?.error;
      throw new ApiError(err?.message ?? `요청에 실패했습니다 (HTTP ${res.status}).`, res.status, err?.code ?? null);
    }
    return body as T;
  }

  return {
    get: <T>(path: string) => request<T>(path),
    post: <T>(path: string, data?: unknown) => request<T>(path, { method: 'POST', body: data === undefined ? undefined : JSON.stringify(data) }),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;

export interface HealthResponse {
  ok: boolean;
  service: string;
  version: string;
  /** 함수 빌드 커밋(7자리, 로컬 미커밋 변경이 있으면 -dirty) */
  commit?: string;
  builtAt?: string | null;
}
